import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { translate } from '@jsverse/transloco';

import { AppSharedModule } from 'app/app-shared.module';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { AlertService } from 'app/services/alert.service';
import {
  GenerateSpotlightDashboardParams,
  GenerateSpotlightDashboardResult,
  SpotlightAgentDashboardIdeasService,
  SpotlightDashboardIdea,
} from 'app/services/spotlight-agent-dashboard-ideas.service';

export type SpotlightDialogMode = 'dashboard' | 'chat';

export interface CollectionOption {
  id: string;
  name: string;
  contentChannel?: string;
  hasSections?: boolean;
}

export interface SpotlightDashboardIdeasDialogData {
  projectId: string;
  collectionId?: string;
  collections?: CollectionOption[];
}

export interface GenerationResult {
  redirectUrl: string | null;
  dashboardName?: string;
}

/** Delays (ms) at which each `generationStages` entry becomes the active stage while ideas are loading. */
const IDEAS_STAGE_DELAYS_MS: ReadonlyArray<number> = [0, 4000];

/** Delays (ms) at which each `generationStages` entry becomes the active stage during dashboard generation. */
const GENERATION_STAGE_DELAYS_MS: ReadonlyArray<number> = [0, 5000, 12000, 25000];

export interface SpotlightDashboardIdeasDialogResult {
  minimized: boolean;
  generationPromise?: Promise<GenerationResult>;
  selectedIdeaTitle?: string;
  chatStarted?: boolean;
}

@Component({
  selector: 'app-spotlight-dashboard-ideas-dialog',
  templateUrl: './spotlight-dashboard-ideas-dialog.component.html',
  styleUrls: ['./spotlight-dashboard-ideas-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SpotlightDashboardIdeasDialogComponent {
  private readonly ideasService = inject(SpotlightAgentDashboardIdeasService);
  private readonly alertService = inject(AlertService);
  private readonly groupService = inject(GroupService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly modalClosed = output<SpotlightDashboardIdeasDialogResult | null>();
  public readonly data = input<SpotlightDashboardIdeasDialogData>();
  public readonly mode = input<SpotlightDialogMode>('dashboard');

  public readonly ideas = signal<SpotlightDashboardIdea[]>([]);
  public readonly isLoadingIdeas = signal<boolean>(false);
  public readonly isGenerating = signal<boolean>(false);
  public readonly selectedPrompt = signal<string | null>(null);
  public readonly selectedIdeaTitle = signal<string | null>(null);
  public readonly customInstruction = signal<string>('');
  public readonly selectedCollection = signal<CollectionOption | null>(null);
  public readonly step = signal<'collection' | 'ideas'>('collection');
  public readonly currentStageIndex = signal<number>(-1);

  public readonly generationStages: ReadonlyArray<{ id: string; key: string }> = [
    { id: 'analyzing', key: 'stage-analyzing' },
    { id: 'structure', key: 'stage-structure' },
    { id: 'widgets', key: 'stage-widgets' },
    { id: 'finalizing', key: 'stage-finalizing' },
  ];

  private phaseTimers: ReturnType<typeof setTimeout>[] = [];

  private generationPromise: Promise<GenerationResult> | null = null;
  private wasMinimized = false;

  public readonly showCollectionStep = computed(() => {
    const dialogData = this.data();
    return !dialogData?.collectionId && (dialogData?.collections?.length ?? 0) > 1;
  });

  public readonly collectionDropdownOptions = computed(() =>
    (this.data()?.collections ?? []).map((collection) => ({
      id: collection.id,
      title: collection.name,
    }))
  );

  public get canGenerate(): boolean {
    return !!this.selectedPrompt() && !this.isGenerating();
  }

  public readonly isChatMode = computed(() => this.mode() === 'chat');

  private onAutoSelectionComplete(): void {
    if (this.mode() === 'chat') {
      this.onBeginChatClick();
      return;
    }
    this.proceedToIdeas();
  }

  private readonly cleanupOnDestroy = this.destroyRef.onDestroy(() => this.clearStageTimers());

  private readonly initialSelectionEffect = effect(() => {
    const dialogData = this.data();
    if (!dialogData) {
      return;
    }

    // For chat mode, skip the modal entirely and open chat drawer directly
    if (this.mode() === 'chat') {
      this.openChatDrawerDirectly(dialogData);
      return;
    }

    if (dialogData.collectionId) {
      this.selectedCollection.set({
        id: dialogData.collectionId,
        name: dialogData.collectionId,
      });
      this.onAutoSelectionComplete();
      return;
    }

    if (dialogData.collections?.length === 1) {
      this.selectedCollection.set(dialogData.collections[0]);
      this.onAutoSelectionComplete();
      return;
    }

    this.step.set('collection');
  });

  private openChatDrawerDirectly(dialogData: SpotlightDashboardIdeasDialogData): void {
    // Dispatch event to open AI assistant drawer directly with collection selector
    window.dispatchEvent(
      new CustomEvent('open-ai-assistant', {
        detail: {
          fullscreen: false,
          mode: 'ask',
          projectId: dialogData.projectId,
        },
      })
    );
    this.modalClosed.emit({ minimized: false, chatStarted: true });
  }

  public onCollectionDropdownChange(selected: { id: string; title: string }): void {
    const nextSelection = (this.data()?.collections ?? []).find((c) => c.id === selected?.id);
    if (nextSelection) {
      this.selectedCollection.set(nextSelection);
    }
  }

  public async proceedToIdeas(): Promise<void> {
    const selected = this.selectedCollection();
    const dialogData = this.data();
    if (!selected || !dialogData) {
      return;
    }
    this.step.set('ideas');
    this.isLoadingIdeas.set(true);
    this.advanceStagesOverTime(IDEAS_STAGE_DELAYS_MS);
    try {
      const fetchedIdeas = await this.ideasService.fetchIdeas(
        selected.id,
        dialogData.projectId,
        selected.contentChannel,
        selected.hasSections
      );
      this.ideas.set(fetchedIdeas);
    } catch (error: unknown) {
      this.alertService.showErrorToast(
        translate('project.spotlight-dashboard-ideas-dialog.load-error'),
        error
      );
      this.modalClosed.emit(null);
      return;
    } finally {
      this.clearStageTimers();
      this.isLoadingIdeas.set(false);
    }
  }

  private clearStageTimers(): void {
    for (const timer of this.phaseTimers) {
      clearTimeout(timer);
    }
    this.phaseTimers = [];
    this.currentStageIndex.set(-1);
  }

  /**
   * Optimistically walk through generation stages on a fixed schedule.
   * Each entry is the start delay (ms) at which the corresponding stage
   * becomes the active one; previous stages are marked complete.
   */
  private advanceStagesOverTime(delaysMs: ReadonlyArray<number>): void {
    this.clearStageTimers();
    delaysMs.forEach((delayMs: number, index: number) => {
      const timer = setTimeout(() => this.currentStageIndex.set(index), delayMs);
      this.phaseTimers.push(timer);
    });
  }

  public goBackToCollections(): void {
    this.step.set('collection');
    this.ideas.set([]);
    this.selectedPrompt.set(null);
    this.selectedIdeaTitle.set(null);
    this.customInstruction.set('');
  }

  public onSelectPrompt(idea: SpotlightDashboardIdea): void {
    this.selectedPrompt.set(idea.prompt);
    this.selectedIdeaTitle.set(idea.title);
  }

  public onCustomInstructionChange(value: string): void {
    this.customInstruction.set(value);
    if (value?.trim().length) {
      this.selectedPrompt.set(value);
      this.selectedIdeaTitle.set(null);
    }
  }

  public async onGenerateClick(): Promise<void> {
    if (!this.canGenerate || !this.selectedCollection()) {
      return;
    }
    this.isGenerating.set(true);
    const prompt: string = this.selectedPrompt() as string;
    const selected = this.selectedCollection()!;
    const params: GenerateSpotlightDashboardParams = {
      projectId: this.data()?.projectId ?? '',
      collectionId: selected.id,
      prompt,
      contentChannel: selected.contentChannel,
      hasSections: selected.hasSections,
    };

    this.generationPromise = this.generateDashboard(params);
    const result = await this.generationPromise;
    if (result.redirectUrl && !this.wasMinimized) {
      this.modalClosed.emit({ minimized: false });
      window.location.href = result.redirectUrl;
    }
  }

  /**
   * Minimize the dialog while a dashboard is being generated and
   * hand the pending generation promise back to the parent so it
   * can show background toasts and handle redirect when ready.
   */
  public onMinimize(): void {
    if (this.generationPromise) {
      this.wasMinimized = true;
      this.modalClosed.emit({
        minimized: true,
        generationPromise: this.generationPromise,
        selectedIdeaTitle: this.selectedIdeaTitle(),
      });
    }
  }

  private async generateDashboard(
    params: GenerateSpotlightDashboardParams
  ): Promise<GenerationResult> {
    this.advanceStagesOverTime(GENERATION_STAGE_DELAYS_MS);
    try {
      const result: GenerateSpotlightDashboardResult =
        await this.ideasService.generateSpotlightDashboard(params);
      if (result.dashboardUrl) {
        return {
          redirectUrl: this.buildRedirectUrl(result.dashboardUrl),
          dashboardName: result.dashboardName || this.selectedIdeaTitle(),
        };
      }
      return { redirectUrl: null };
    } catch (error: unknown) {
      this.alertService.showErrorToast(
        translate('project.spotlight-dashboard-ideas-dialog.generate-error'),
        error
      );
      return { redirectUrl: null };
    } finally {
      this.clearStageTimers();
      this.isGenerating.set(false);
    }
  }

  public onCancel(): void {
    this.modalClosed.emit(null);
  }

  public async onBeginChatClick(): Promise<void> {
    const selected = this.selectedCollection();
    const dialogData = this.data();
    if (!selected || !dialogData) {
      return;
    }

    // Dispatch event to open AI assistant drawer instead of new tab
    window.dispatchEvent(
      new CustomEvent('open-ai-assistant', {
        detail: {
          fullscreen: false,
          mode: 'ask',
          projectId: dialogData.projectId,
          collectionId: selected.id,
        },
      })
    );

    this.modalClosed.emit({ minimized: false, chatStarted: true });
  }

  private buildRedirectUrl(sourceUrl: string): string | null {
    try {
      const parsedUrl: URL = new URL(sourceUrl);
      const segments: string[] = parsedUrl.pathname
        .split('/')
        .filter((segment: string) => segment.length > 0);
      const dashboardsIndex: number = segments.indexOf('dashboards');
      if (dashboardsIndex !== -1 && dashboardsIndex + 1 < segments.length) {
        const dashboardId: string = segments[dashboardsIndex + 1];
        const projectId = this.data()?.projectId ?? this.data?.().projectId;
        const groupId: string = this.groupService.getSelectedGroupId(true);
        return `/groups/${groupId}/projects/${projectId}/dashboards/${dashboardId}`;
      }
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return sourceUrl;
    }
  }
}
